import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type {
  FoundingMemberProgress,
  FoundingMemberThreshold,
} from '@/types/founding-member';

export function useFoundingMemberProgress() {
  const { user } = useAuth();

  const query = useQuery<FoundingMemberProgress | null>({
    queryKey: ['founding-member-progress', user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('founding_member_progress')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data as FoundingMemberProgress | null;
    },
    enabled: !!user,
  });

  const thresholds: FoundingMemberThreshold[] = query.data
    ? [
        {
          path: 'streak_30',
          label: '30-day habit streak',
          current: query.data.longest_streak,
          target: 30,
        },
        {
          path: 'goals_3',
          label: '3 goals completed',
          current: query.data.goals_completed,
          target: 3,
        },
        {
          path: 'feedback_10',
          label: '10 feedback submissions',
          current: query.data.feedback_count,
          target: 10,
        },
        {
          path: 'referrals_5',
          label: '5 referrals',
          current: query.data.referral_count,
          target: 5,
        },
      ]
    : [];

  return {
    progress: query.data ?? null,
    thresholds,
    isQualified: query.data?.is_qualified ?? false,
    qualificationPath: query.data?.qualification_path ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}
